import { useEffect, useState } from 'react';

/**
 * The current instant, re-rendering once a second.
 *
 * This drives a WALL CLOCK, which is display only. It is deliberately not the source of any
 * figure that reaches payroll: the server owns every hour and lunch calculation (D-03 /
 * T-22-payroll), and the card shows what the server booked.
 *
 * Ticks are aligned to the next real second rather than fired on a fixed 1000ms interval, so the
 * seconds digit changes when the clock actually changes instead of drifting a few hundred
 * milliseconds off and visibly skipping a number.
 */
export function useNow(): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const scheduleNextTick = () => {
      const current = new Date();
      setNow(current);
      // Time until the top of the next second. Clamped to 1ms so a pathological clock cannot
      // spin this into a busy loop.
      const untilNextSecond = Math.max(1, 1000 - current.getMilliseconds());
      timer = setTimeout(scheduleNextTick, untilNextSecond);
    };

    scheduleNextTick();
    return () => clearTimeout(timer);
  }, []);

  return now;
}

/**
 * Whole minutes elapsed since an ISO instant, or null if it cannot be parsed.
 *
 * MINUTE resolution is the point. A second-by-second shift timer on a payroll screen invites
 * someone to read pay off it; hours and minutes read as status. The authoritative figure is
 * whatever the server books at clock-out, which applies the lunch rule this cannot see.
 */
export function elapsedMinutesSince(iso: string, now: Date): number | null {
  const started = new Date(iso);
  const startedMs = started.getTime();
  if (Number.isNaN(startedMs)) return null;
  const minutes = Math.floor((now.getTime() - startedMs) / 60_000);
  // A clock-in stamped slightly ahead of the browser (server skew) must read 0, never negative.
  return minutes < 0 ? 0 : minutes;
}
